// Talking to the Worker.
//
// The API token is typed in once and kept in IndexedDB rather than compiled
// into the bundle, so the deployed JavaScript carries no credential and the
// service worker can read the same value when it drains the queue in the
// background.

import { metaGet, metaSet, metaDelete } from './idb';

export const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

const TOKEN_KEY = 'api_token';

let cachedToken: string | null = null;

export async function loadToken(): Promise<string | null> {
  if (cachedToken !== null) return cachedToken;
  cachedToken = (await metaGet<string>(TOKEN_KEY)) ?? null;
  return cachedToken;
}

export async function saveToken(token: string): Promise<void> {
  cachedToken = token.trim();
  await metaSet(TOKEN_KEY, cachedToken);
  // the service worker reads these two to sync while the app is closed
  await metaSet('api_base', API_BASE);
}

export async function forgetToken(): Promise<void> {
  cachedToken = null;
  await metaDelete(TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
  /** 4xx: the request will never succeed, so a queued op should be dropped. */
  get permanent(): boolean {
    return this.status >= 400 && this.status < 500;
  }
}

/** Network failure, as distinct from a server that answered with an error. */
export class OfflineError extends Error {}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await loadToken();
  if (!token) throw new ApiError('not paired', 401);

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new OfflineError(`cannot reach the server`);
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const data: unknown = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const message =
      data && typeof data === 'object' && 'error' in data
        ? String((data as { error: unknown }).error)
        : `${method} ${path} failed`;
    throw new ApiError(message, res.status);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};

/** Does this token work? Used by the pairing screen. */
export async function checkToken(token: string): Promise<'ok' | 'bad-token' | 'offline'> {
  try {
    const res = await fetch(`${API_BASE}/api/sessions?limit=1`, {
      headers: { Authorization: `Bearer ${token.trim()}` },
    });
    if (res.status === 401) return 'bad-token';
    return res.ok ? 'ok' : 'bad-token';
  } catch {
    return 'offline';
  }
}
